import { eventStatuses } from "../Constants";

/** @param {unknown} event */
export const readEventStatus = (event) => {
  if (event == null || typeof event !== "object") return "";
  return event.status ?? event.attributes?.status ?? "";
};

/** @param {unknown} status */
export const normalizePortalEventStatus = (status) =>
  String(status ?? "").trim().toLowerCase();

/** Company portal edit: staffing / recurrence restrictions while event is running. */
export const isPortalEventOngoing = (status) =>
  normalizePortalEventStatus(status) === eventStatuses.ongoing;

export const isPortalEventUpcoming = (status) =>
  normalizePortalEventStatus(status) === eventStatuses.upcoming;

export const isPortalEventPast = (status) =>
  normalizePortalEventStatus(status) === eventStatuses.past;

export const isPortalEventCompleted = (status) => {
  const normalized = normalizePortalEventStatus(status);
  return (
    normalized === eventStatuses.past ||
    normalized === "completed" ||
    normalized === "complete"
  );
};

/** Joined-tab check-in/out summary counts — ongoing events only. */
export const shouldShowCheckInSummary = (status) =>
  isPortalEventOngoing(status);

/** Joined-tab check-in filter — ongoing and completed events. */
export const shouldShowJoinedCheckInFilter = (status) =>
  isPortalEventOngoing(status) || isPortalEventCompleted(status);

/** Evaluations tab is only shown for ongoing and completed (past) events. */
export const shouldShowEventEvaluationsTab = (eventOrStatus) => {
  const status =
    typeof eventOrStatus === "object" && eventOrStatus !== null
      ? readEventStatus(eventOrStatus)
      : eventOrStatus;
  return isPortalEventOngoing(status) || isPortalEventCompleted(status);
};

/** Payroll tab is only shown for ongoing and completed (past) events. */
export const shouldShowEventPayrollTab = (eventOrStatus) => {
  const status =
    typeof eventOrStatus === "object" && eventOrStatus !== null
      ? readEventStatus(eventOrStatus)
      : eventOrStatus;
  return isPortalEventOngoing(status) || isPortalEventCompleted(status);
};

/** Applicants tab is hidden while the event is still pending (draft). */
export const shouldShowEventApplicantsTab = (eventOrStatus) => {
  const status =
    typeof eventOrStatus === "object" && eventOrStatus !== null
      ? readEventStatus(eventOrStatus)
      : eventOrStatus;
  const normalized = normalizePortalEventStatus(status);
  if (!normalized) return false;
  return normalized !== eventStatuses.pending;
};

export const isPortalEventCancelled = (status) => {
  const normalized = normalizePortalEventStatus(status);
  return (
    normalized === eventStatuses.cancelled || normalized === "cancelled"
  );
};

/** Completed/cancelled events: only export and note/tag edits are allowed. */
export const isApplicantsActionsLocked = (eventOrStatus) => {
  const status =
    typeof eventOrStatus === "object" && eventOrStatus !== null
      ? readEventStatus(eventOrStatus)
      : eventOrStatus;

  return isPortalEventCompleted(status) || isPortalEventCancelled(status);
};

/** Company portal edit: role removal restrictions apply before and during execution. */
export const isPortalEventOngoingOrUpcoming = (status) => {
  const normalized = normalizePortalEventStatus(status);
  return (
    normalized === eventStatuses.ongoing ||
    normalized === eventStatuses.upcoming
  );
};

/**
 * Locks schedule pickers tied to actual event start (build-up / event / dismantling per product spec).
 * @param {string} isoStart Event start ISO-like string from form/API
 * @param {number} hoursBefore Lock editing when `now >= start - hoursBefore`
 */
export const isTimelineLockedNearEventStart = (isoStart, hoursBefore = 2) => {
  if (!isoStart) return false;
  const t = new Date(isoStart).getTime();
  if (!Number.isFinite(t)) return false;
  return Date.now() >= t - hoursBefore * 60 * 60 * 1000;
};

export const isRoleRemovalLockedByEventTiming = (status, eventStartAt) => {
  if (isPortalEventOngoing(status) || isPortalEventPast(status)) return true;
  if (isPortalEventUpcoming(status)) {
    return isTimelineLockedNearEventStart(eventStartAt);
  }
  return false;
};

/**
 * Paid training edits allowed until `hoursBefore` before training `starts_at`.
 */
export const isPaidTrainingTimelineLocked = (trainingStartsAt, hoursBefore = 2) =>
  isTimelineLockedNearEventStart(trainingStartsAt, hoursBefore);
